import { useEffect, useRef } from "react";
import { useLawExplorer } from "../hooks/useLawExplorer";
import { LawExplorerHeader } from "./LawExplorerHeader";
import { LawExplorerViewer } from "./LawExplorerViewer";

interface LawExplorerProps {
  articleId: string | null;
  onClose: () => void;
}

export function LawExplorer({ articleId, onClose }: LawExplorerProps) {
  const { state, navigateTo, goBack, canGoBack } = useLawExplorer(articleId);
  const isOpen = articleId !== null;
  const touchStartX = useRef<number | null>(null);

  // Swipe right to close (mobile)
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (touch) {
      touchStartX.current = touch.clientX;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const touch = e.changedTouches[0];
    if (!touch) return;
    const deltaX = touch.clientX - touchStartX.current;
    // Swipe right > 100px triggers close
    if (deltaX > 100) {
      onClose();
    }
    touchStartX.current = null;
  };

  // Prevent body scroll when explorer is open (mobile)
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("law-explorer-open");
    } else {
      document.body.classList.remove("law-explorer-open");
    }

    return () => {
      document.body.classList.remove("law-explorer-open");
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <>
      {/* Overlay (mobile) */}
      <div
        className={`law-explorer-overlay ${isOpen ? "is-open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <aside
        className={`law-explorer ${isOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Explorateur juridique"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <LawExplorerHeader
          item={state.currentItem}
          isLoading={state.isLoading}
          canGoBack={canGoBack}
          onNavigate={navigateTo}
          onGoBack={goBack}
          onClose={onClose}
        />
        <LawExplorerViewer
          item={state.currentItem}
          isLoading={state.isLoading}
          error={state.error}
          onNavigate={navigateTo}
        />
      </aside>
    </>
  );
}
